import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import avatar1 from "../../components/dashboard/img/avatars/1.png";
import avatar5 from "../../components/dashboard/img/avatars/5.png";
import avatar6 from "../../components/dashboard/img/avatars/6.png";
import screen5 from "../../components/dashboard/img/layouts/tela_dashboard.png";
import screen6 from "../../components/dashboard/img/layouts/tala_de_upload.png";
import screen7 from "../../components/dashboard/img/layouts/tela_de_agenda.png";
import screen8 from "../../components/dashboard/img/layouts/tela_notificacao.png";
import HeroThreeScene from "../../components/landing/HeroThreeScene";
import {
  FiAlertTriangle,
  FiBarChart2,
  FiBell,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiGrid,
  FiMail,
  FiMessageSquare,
  FiShield,
  FiTarget,
  FiTrendingUp,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";
import { FaFacebookF, FaGoogle, FaInstagram, FaMicrosoft, FaWhatsapp } from "react-icons/fa";
import { MdSms } from "react-icons/md";
import { SiGmail, SiGooglecalendar } from "react-icons/si";

const painPoints = [
  {
    icon: FiAlertTriangle,
    title: "Contrato vence e ninguém percebe",
    text: "O prazo passa, o jurídico descobre tarde e o prejuízo já aconteceu.",
  },
  {
    icon: FiGrid,
    title: "Planilha espalhada em várias áreas",
    text: "Cada unidade controla do seu jeito e a visão da operação desaparece.",
  },
  {
    icon: FiUsers,
    title: "Responsável não está claro",
    text: "Quando surge urgência, o time perde tempo procurando quem aprova e quem assina.",
  },
  {
    icon: FiFileText,
    title: "Auditoria vira retrabalho",
    text: "Documentos, alertas e histórico ficam dispersos, sem trilha confiável.",
  },
];

const featureCards = [
  {
    icon: FiUserCheck,
    title: "Signatários com rastreabilidade",
    text: "Defina responsáveis e mantenha a trilha de quem aprova, acompanha e responde.",
  },
  {
    icon: FiGrid,
    title: "Estrutura por empresa, região e unidade",
    text: "Organize contratos conforme a operação real do seu negócio.",
  },
  {
    icon: FiBell,
    title: "Alertas automáticos",
    text: "Ative notificações por e-mail e SMS antes do prazo ficar crítico.",
  },
  {
    icon: FiCalendar,
    title: "Agenda executiva de vencimentos",
    text: "Visualize renovações, pendências e próximos marcos em um calendário claro.",
  },
  {
    icon: FiBarChart2,
    title: "Dashboard para decisão",
    text: "Acompanhe contratos ativos, vencidos e renovados com visão consolidada.",
  },
  {
    icon: FiShield,
    title: "Governança sem improviso",
    text: "Padronize o fluxo contratual sem depender de memória, planilha ou cobrança manual.",
  },
];

const workflowSteps = [
  {
    label: "01",
    title: "Estruture a base",
    text: "Cadastre signatários, regiões, unidades e empresas conforme a operação.",
  },
  {
    label: "02",
    title: "Suba o contrato",
    text: "Faça o upload do documento e centralize o histórico em um único lugar.",
  },
  {
    label: "03",
    title: "Ative os alertas",
    text: "Defina as notificações para o time agir antes do vencimento virar problema.",
  },
  {
    label: "04",
    title: "Acompanhe pela agenda",
    text: "Use a agenda de contratos para priorizar renovações e pendências com antecedência.",
  },
];

const proofMetrics = [
  { value: "100%", label: "visão centralizada da operação contratual" },
  { value: "1", label: "fluxo padrão para jurídico, administrativo e gestão" },
  { value: "24/7", label: "monitoramento dos prazos mais críticos" },
];

const benefits = [
  "Menos risco de vencimento perdido e renovação fora do prazo.",
  "Mais clareza sobre quem precisa agir em cada contrato.",
  "Tempo reduzido para localizar documentos e histórico operacional.",
  "Gestão mais confiável para auditoria, liderança e expansão da operação.",
];

const faqs = [
  {
    q: "Preciso instalar algo na empresa?",
    a: "Não. A plataforma é 100% web, com acesso por login seguro e controle de permissões.",
  },
  {
    q: "Consigo operar várias empresas, regiões e unidades?",
    a: "Sim. A estrutura foi desenhada para gestão hierárquica, com visão consolidada e controle por contexto operacional.",
  },
  {
    q: "Os alertas de vencimento são automáticos?",
    a: "Sim. O sistema permite ativar alertas por e-mail e SMS para reduzir risco de esquecimento e atraso.",
  },
];

const revealUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.55, ease: "easeOut" as const },
};

export default function LandingPage() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    onResize();
    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className="landing-page lp3">
      <section id="hero" className="lp3-hero">
        <div className="lp3-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lp3-left"
          >
            <span className="lp3-tag"><FiShield /> Gestão contratual para operação que não pode perder prazo</span>
            <h1>Troque planilha, urgência e contrato vencido por uma operação previsível.</h1>
            <p>
              O BIM Contratos centraliza empresas, regiões, unidades, signatários, documentos e alertas
              para sua equipe acompanhar vencimentos com clareza, agir antes do risco e manter governança.
            </p>

            <div className="landing-cta">
              <Link to="/auth?mode=register" className="btn lp3-primary-btn btn-lg">Começar agora</Link>
              <Link to="/auth" className="btn lp3-secondary-btn btn-lg">Entrar na plataforma</Link>
            </div>

            <div className="lp3-hero-points">
              <span><FiCheckCircle /> Controle por empresa, região e unidade</span>
              <span><FiCheckCircle /> Alertas automáticos por e-mail e SMS</span>
              <span><FiCheckCircle /> Agenda focada em renovação e vencimento</span>
            </div>

            <div className="lp3-proof-line">
              <span className="lp3-avatar-stack">
                <img src={avatar1} alt="Usuário 1" />
                <img src={avatar5} alt="Usuário 2" />
                <img src={avatar6} alt="Usuário 3" />
              </span>
              <span>Feito para times administrativos, jurídicos e operacionais que precisam responder rápido.</span>
            </div>
          </motion.div>

          <motion.div
            className="lp3-right"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {!isMobile ? (
              <div className="contract-3d-scene">
                <HeroThreeScene />
                <div className="lp3-integration-icon gmail"><SiGmail /></div>
                <div className="lp3-integration-icon outlook"><FaMicrosoft /></div>
                <div className="lp3-integration-icon agenda"><SiGooglecalendar /></div>
                <div className="lp3-integration-icon sms"><MdSms /></div>
              </div>
            ) : null}

            <div className="lp3-executive-board">
              {proofMetrics.map((item) => (
                <div key={item.label} className="lp3-executive-metric">
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            <div className="lp3-glass-panel">
              <div><FiMail /> <strong>Alerta por e-mail</strong></div>
              <div><FiMessageSquare /> <strong>SMS para o time</strong></div>
              <div><FiTarget /> <strong>Agenda contratual</strong></div>
              <div><FiTrendingUp /> <strong>Histórico para decisão</strong></div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="dor" className="landing-section lp2-section lp3-pain-section">
        <motion.div className="lp2-section-head" {...revealUp}>
          <h2 className="lp3-title-animated">A dor não é o contrato. É a falta de controle antes do vencimento.</h2>
          <p>Quando a gestão depende de planilha, memória ou cobrança manual, o problema já começou.</p>
        </motion.div>
        <div className="lp3-pain-grid">
          {painPoints.map((item, idx) => (
            <motion.article
              key={item.title}
              className="landing-card lp3-pain-card lp3-card-animated"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.42, delay: idx * 0.07 }}
              whileHover={{ y: -4 }}
            >
              <span className="lp3-feature-icon"><item.icon /></span>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section id="valor" className="landing-section lp2-section lp3-value-section">
        <motion.div className="lp2-section-head" {...revealUp}>
          <h2 className="lp3-title-animated">Uma rotina clara para cadastrar, acompanhar e renovar no tempo certo.</h2>
          <p>O sistema organiza a operação contratual em um fluxo simples, rastreável e replicável.</p>
        </motion.div>
        <div className="lp3-process-grid">
          {workflowSteps.map((item, idx) => (
            <motion.article
              key={item.label}
              className="landing-card lp3-process-card"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.42, delay: idx * 0.08 }}
            >
              <span className="lp3-process-number">{item.label}</span>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section id="recursos" className="landing-section lp2-section">
        <motion.div className="lp2-section-head" {...revealUp}>
          <h2 className="lp3-title-animated">Recursos pensados para reduzir risco e aumentar resposta da operação.</h2>
          <p>Não é só armazenar contrato. É dar contexto, prioridade e ação para o time certo.</p>
        </motion.div>
        <div className="lp2-feature-grid">
          {featureCards.map((item, idx) => (
            <motion.article
              key={item.title}
              className="landing-card lp2-feature-card lp3-card-animated"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.18 }}
              transition={{ duration: 0.42, delay: idx * 0.06 }}
              whileHover={{ y: -4, scale: 1.01 }}
            >
              <span className="lp3-feature-icon"><item.icon /></span>
              <h6>{item.title}</h6>
              <p>{item.text}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="landing-section lp2-section lp3-integrations">
        <motion.div className="lp2-section-head" {...revealUp}>
          <h2 className="lp3-title-animated">Acompanhamento contínuo, não conferência tardia.</h2>
          <p>Os alertas circulam nos canais do dia a dia para o contrato não desaparecer da rotina do time.</p>
        </motion.div>
        <div className="lp3-marquee">
          <div className="lp3-marquee-track">
            <span><FaGoogle /> Gmail</span>
            <span><FiMessageSquare /> SMS</span>
            <span><FaMicrosoft /> Outlook</span>
            <span><FiCalendar /> Google Agenda</span>
            <span><FiMail /> E-mail corporativo</span>
            <span><FiBell /> Alerta automático</span>
            <span><FaGoogle /> Gmail</span>
            <span><FiMessageSquare /> SMS</span>
            <span><FaMicrosoft /> Outlook</span>
            <span><FiCalendar /> Google Agenda</span>
          </div>
        </div>
      </section>

      <section id="preview" className="landing-section lp2-section">
        <motion.div className="lp2-dashboard-preview landing-card lp3-preview-shell" {...revealUp}>
          <div className="lp3-preview-copy">
            <h3>O que a sua operação passa a enxergar</h3>
            <p>
              Dashboard, upload, agenda e notificações foram desenhados para mostrar o que vence,
              quem precisa agir e qual contrato exige decisão agora.
            </p>
            <div className="d-flex gap-2 flex-wrap">
              <span className="lp2-chip">Visão consolidada</span>
              <span className="lp2-chip">Prioridade por vencimento</span>
              <span className="lp2-chip">Estrutura operacional</span>
              <span className="lp2-chip">Histórico rastreável</span>
            </div>
          </div>

          <div className="lp3-system-gallery">
            <motion.article className="lp3-system-shot" whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
              <header>Dashboard Operacional</header>
              <img src={screen5} alt="Tela real do dashboard operacional" loading="lazy" />
            </motion.article>
            <motion.article className="lp3-system-shot" whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
              <header>Upload de Documento</header>
              <img src={screen6} alt="Tela real de upload de documento" loading="lazy" />
            </motion.article>
            <motion.article className="lp3-system-shot" whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
              <header>Agenda de Contratos</header>
              <img src={screen7} alt="Tela real da agenda de contratos" loading="lazy" />
            </motion.article>
            <motion.article className="lp3-system-shot" whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
              <header>Central de Notificações</header>
              <img src={screen8} alt="Tela real da central de notificações" loading="lazy" />
            </motion.article>
          </div>
        </motion.div>
      </section>

      <section id="beneficios" className="landing-section lp2-section lp2-alt">
        <div className="lp2-benefits-wrap lp3-results-wrap">
          <motion.div {...revealUp}>
            <h2 className="lp3-title-animated">Resultado percebido pela liderança e pelo time.</h2>
            <p>Menos urgência, mais previsibilidade e uma base contratual pronta para crescer sem improviso.</p>
            <Link to="/auth?mode=register" className="btn modern-primary-btn">Estruturar minha operação</Link>
          </motion.div>
          <div className="lp2-benefit-list">
            {benefits.map((item, idx) => (
              <motion.div
                key={item}
                className="landing-card lp2-benefit-item lp3-card-animated"
                initial={{ opacity: 0, x: 18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.42, delay: idx * 0.07 }}
              >
                {item}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="seguranca" className="landing-section lp2-section">
        <div className="lp3-security-wrap">
          <motion.article className="landing-card lp3-testimonial-card" {...revealUp}>
            <div className="lp3-quote">"</div>
            <p>
              Antes, cada unidade controlava os contratos de um jeito. Hoje temos visão centralizada,
              responsáveis definidos e alertas que antecipam a renovação.
            </p>
            <div className="lp3-testimonial-author">
              <img src={avatar5} alt="Carla Ribeiro" />
              <div>
                <strong>CARLOS RIBEIRO</strong>
                <small>Coordenadora Administrativa</small>
              </div>
            </div>
          </motion.article>

          <motion.article className="landing-card lp3-methods-card" {...revealUp}>
            <h3>O sistema cobre o que mais pesa na gestão contratual</h3>
            <p>Cadastro, organização estrutural, envio de alertas e acompanhamento por agenda em um único fluxo.</p>
            <div className="lp3-methods-grid">
              <span><FiUserCheck /> Signatários vinculados ao processo</span>
              <span><FiGrid /> Empresas, regiões e unidades</span>
              <span><FiClock /> Prazos com antecedência operacional</span>
              <span><FiMail /> Alertas por e-mail</span>
              <span><MdSms /> Alertas por SMS</span>
              <span><FiCalendar /> Agenda de vencimentos e renovações</span>
            </div>
          </motion.article>
        </div>
      </section>

      <section id="faq" className="landing-section lp2-section">
        <motion.h2 className="lp3-title-animated" {...revealUp}>Perguntas frequentes</motion.h2>
        <div className="landing-faq">
          {faqs.map((item, idx) => (
            <motion.details
              key={item.q}
              className="landing-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.38, delay: idx * 0.06 }}
            >
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </motion.details>
          ))}
        </div>
      </section>

      <motion.footer id="contato" className="landing-footer lp2-footer lp3-footer" {...revealUp}>
        <div className="lp3-footer-cta">
          <div className="lp3-footer-main">
            <h4>Profissionalize a gestão de contratos antes que o próximo prazo vire urgência.</h4>
            <p className="mb-0">Centralize sua estrutura, automatize alertas e acompanhe vencimentos com visão executiva.</p>
          </div>
          <Link to="/auth?mode=register" className="btn modern-primary-btn">Criar conta</Link>
        </div>

        <div className="lp3-footer-grid">
          <div>
            <h6>BIM CONTRATOS</h6>
            <p>Plataforma para controlar contratos, vencimentos, signatários e alertas com governança operacional.</p>
          </div>
          <div>
            <h6>Navegação</h6>
            <a href="#hero">Início</a>
            <a href="#dor">Dores</a>
            <a href="#recursos">Recursos</a>
            <a href="#faq">FAQ</a>
          </div>
          <div>
            <h6>Contato</h6>
            <a href="mailto:contato@byteinmotion.com.br">contato@byteinmotion.com.br</a>
          </div>
          <div>
            <h6>Redes sociais</h6>
            <div className="lp3-social-links">
              <a href="https://www.facebook.com/byteinmotion" target="_blank" rel="noreferrer" aria-label="Facebook da empresa">
                <FaFacebookF />
              </a>
              <a href="https://www.instagram.com/byteinmotion" target="_blank" rel="noreferrer" aria-label="Instagram da empresa">
                <FaInstagram />
              </a>
            </div>
          </div>
        </div>

        <div className="lp3-copyright">© 2026 Byte in Motion. Todos os direitos reservados.</div>
      </motion.footer>

      <a
        className="lp3-whatsapp-fab"
        href="https://wa.me/5519992103070"
        target="_blank"
        rel="noreferrer"
        aria-label="Falar no WhatsApp"
      >
        <FaWhatsapp />
      </a>
    </div>
  );
}
